import { isFormattedNumber } from 'customer-data-objects/property/PropertyIdentifier';
import { useCallback, memo } from 'react';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import UINumberInput from 'UIComponents/input/UINumberInput';
import UITextInput from 'UIComponents/input/UITextInput';
import { getTransferrableProps } from '../utils/getTransferrableProps';
import { parseNumericValue } from '../utils/parseNumericValue';
import { jsx as _jsx } from "react/jsx-runtime";
const BLANK_REGEX = /^\s*$/;
const INVALID_CHARS_REGEX = new RegExp(`[^0-9.-]`, 'g');

/**
 * Best-effort attempt to block users from entering any characters in the input
 * that would generate an invalid number. It is best-effort because some strings
 * which are invalid numbers (e.g., '-1-23...4') can still be entered.
 *
 * We can't reuse `UIAbstractNumberInput`'s trick of parsing the value to a Number
 * and converting it back to a string to strip invalid characters, because
 * unformatted numbers may contain arbitrarily large values that result in
 * overflows (exponential notation or Infinity!) `BigInt` doesn't help either,
 * as unformatted numbers are allowed to contain decimals which BigInt can't
 * parse.
 *
 * The invalid characters regex is copied from `UIAbstractNumberInput` but
 * modified. Regardless of the user's locale or number format preferences, BE
 * validation only allows number properties to include: an optional leading
 * negative sign (-), digits, an optional decimal point (.), and optional
 * trailing digits after the decimal.
 *
 * We do not support I18n/localized parsing, so U+2212 (MINUS SIGN) (commonly
 * used in German instead of U+002D HYPHEN MINUS) and commas (decimal separator
 * in some number formats) are NOT allowed.
 *
 * See https://product.hubteam.com/docs/crm-properties/values/number.html
 * See https://git.hubteam.com/HubSpot/UIComponents/blob/b2767bc90959e927f4ca053fd1036de858d9d7b3/UIComponents/static-3.3455/js/input/abstract/UIAbstractNumberInput.tsx#L40-L71
 */
const removeInvalidCharacters = value => {
  const sanitizedValue = value.replace(INVALID_CHARS_REGEX, '');
  return BLANK_REGEX.test(sanitizedValue) ? null : sanitizedValue;
};
const PropertyInputNumber = props => {
  const {
    value,
    onChange,
    inputRef,
    property,
    precision = 5
  } = props;
  const handleFormattedChange = useCallback(evt => {
    if (typeof onChange === 'function') {
      // UINumberInput's onChange sends `number | null`; convert to `string | null`
      onChange(evt.target.value == null ? evt : SyntheticEvent(String(evt.target.value)));
    }
  }, [onChange]);
  const handleUnformattedChange = useCallback(evt => {
    if (typeof onChange === 'function') {
      onChange(evt.target.value == null ? evt : SyntheticEvent(removeInvalidCharacters(evt.target.value)));
    }
  }, [onChange]);
  return isFormattedNumber(property) ? /*#__PURE__*/_jsx(UINumberInput, Object.assign({}, getTransferrableProps(props), {
    precision: precision
    // @ts-expect-error UINumberInput propTypes allow 'null', but TS types do not; see https://git.hubteam.com/HubSpot/UIComponents/issues/10822
    ,
    value: parseNumericValue(value),
    onChange: handleFormattedChange,
    inputRef: inputRef
  })) : /*#__PURE__*/_jsx(UITextInput, Object.assign({}, getTransferrableProps(props), {
    // null and undefined should display as an empty string so this remains a controlled input
    value: value == null ? '' : `${value}`,
    onChange: handleUnformattedChange,
    inputRef: inputRef
  }));
};
export default /*#__PURE__*/memo(PropertyInputNumber);